/**
 * Saneamiento del cadJson recién parseado del DXF.
 *
 * Aplica DOS filtros en orden:
 *
 * 1. **Polilíneas con vertex en (0,0)** — descartadas. En diseños DXF reales
 *    el origen del mundo es un lugar arbitrario donde raramente pasan
 *    polilíneas legítimas. Los XREF/INSERTs de AutoCAD suelen dejar artefactos
 *    en la capa "0" con un vertex en (0,0) y otro en el punto de inserción —
 *    invisibles en AutoCAD pero presentes en el cadJson, y al rendererlos
 *    crean "líneas hacia el origen" que ensucian la vista.
 *
 * 2. **Entities NO-polilínea fuera del cluster** — descartadas. Tomamos el
 *    bbox del cluster principal (calculado desde las polilíneas que pasaron
 *    el filtro 1) y descartamos cualquier ARC / CIRCLE / INSERT / TEXT /
 *    MTEXT / etc. cuya posición esté fuera de ese bbox + 50% de margen.
 *    Esto elimina "fragmentos" que el dibujante dejó dispersos por error
 *    (típico de capa "0" mal organizada) pero respeta cajetines / leyendas
 *    razonablemente cercanas al diseño.
 *
 * NO TOCA:
 *   - Polilíneas dentro de blocks (cadJson.blocks) — sus coords son relativas
 *     al origen del block, no al mundo.
 *   - Entities sin posición conocida (HATCH, SPLINE complejos) — se dejan
 *     pasar tal cual.
 */

const POLYLINE_TYPES = new Set(['LINE', 'LWPOLYLINE', 'POLYLINE'])

function hasVertexAtOrigin(entity) {
  const verts = entity?.vertices
  if (!verts) return false
  for (const v of verts) {
    if (v?.x === 0 && v?.y === 0) return true
  }
  return false
}

/**
 * Posición característica de una entity NO-polilínea según su tipo.
 * Devuelve null si no podemos derivarla — en ese caso no filtramos.
 */
function getEntityPosition(e) {
  if (e?.position)       return e.position           // INSERT, TEXT, MTEXT, BLOCK
  if (e?.startPoint)     return e.startPoint         // TEXT
  if (e?.center)         return e.center             // CIRCLE, ARC, ELLIPSE
  if (e?.insertionPoint) return e.insertionPoint     // alias
  return null
}

/**
 * @param {Object} cadJson - resultado del parser DXF
 * @param {Object} [opts]
 * @param {number} [opts.outsideMargin=0.5] - margen relativo al tamaño del
 *   cluster para descartar entities fuera (0.5 = 50%). Usar Infinity desactiva
 *   este filtro y aplica solo el de origen.
 * @returns {{
 *   cadJson: Object,
 *   removed: number,
 *   removedPolyArtifacts: number,
 *   removedOutsideCluster: number,
 * }}
 */
export function sanitizeCadJson(cadJson, { outsideMargin = 0.5 } = {}) {
  if (!cadJson?.entities) {
    return { cadJson, removed: 0, removedPolyArtifacts: 0, removedOutsideCluster: 0 }
  }

  const isArr = Array.isArray(cadJson.entities)
  const list  = isArr ? cadJson.entities : Object.values(cadJson.entities)

  // ── Pasada 1: separar polilíneas válidas (= cluster del diseño) del resto ──
  let removedPolyArtifacts = 0
  const survivingPolys = []
  const nonPolys = []
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity

  for (const e of list) {
    if (POLYLINE_TYPES.has(e?.type)) {
      if (hasVertexAtOrigin(e)) {
        removedPolyArtifacts += 1
        continue
      }
      // Expandir bbox del cluster con los vertices de esta polilínea.
      for (const v of (e.vertices || [])) {
        if (Number.isFinite(v?.x) && Number.isFinite(v?.y)) {
          if (v.x < minX) minX = v.x
          if (v.x > maxX) maxX = v.x
          if (v.y < minY) minY = v.y
          if (v.y > maxY) maxY = v.y
        }
      }
      survivingPolys.push(e)
    } else {
      nonPolys.push(e)
    }
  }

  // ── Pasada 2: descartar no-polilíneas fuera del cluster + margen ──
  let removedOutsideCluster = 0
  const survivingNonPolys = []
  const haveCluster = Number.isFinite(minX) && Number.isFinite(outsideMargin)

  if (haveCluster) {
    const pad = Math.max(maxX - minX, maxY - minY) * outsideMargin
    const padMinX = minX - pad, padMaxX = maxX + pad
    const padMinY = minY - pad, padMaxY = maxY + pad

    for (const e of nonPolys) {
      const pos = getEntityPosition(e)
      // Sin posición conocida → no filtramos.
      if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) {
        survivingNonPolys.push(e)
        continue
      }
      if (pos.x < padMinX || pos.x > padMaxX || pos.y < padMinY || pos.y > padMaxY) {
        removedOutsideCluster += 1
        continue
      }
      survivingNonPolys.push(e)
    }
  } else {
    // Sin cluster de referencia (o filtro desactivado) → conservar todo.
    survivingNonPolys.push(...nonPolys)
  }

  const totalRemoved = removedPolyArtifacts + removedOutsideCluster
  if (totalRemoved === 0) {
    return { cadJson, removed: 0, removedPolyArtifacts: 0, removedOutsideCluster: 0 }
  }

  const kept = survivingPolys.concat(survivingNonPolys)
  const nextEntities = isArr
    ? kept
    : Object.fromEntries(kept.map(e => [e.handle ?? e.id, e]))

  return {
    cadJson: { ...cadJson, entities: nextEntities },
    removed: totalRemoved,
    removedPolyArtifacts,
    removedOutsideCluster,
  }
}
